/**
 * Runtime constants for the staging-preview feature.
 *
 * The sessionStorage key lives here so the fetch interceptor (which runs at
 * bootstrap, before React) and the context provider both reference the same
 * literal — a mismatch would silently break the whole flow.
 */

/** sessionStorage key holding the active cycle id. Absence = live mode. */
export const STAGING_CYCLE_STORAGE_KEY = "germanos.staging.cycleId";

/**
 * sessionStorage key holding the HMAC-signed staging token (short-lived, issued
 * by admin-bff). When present the fetch/XHR interceptors send the
 * {@link CYCLE_TOKEN_HEADER} instead of the plain {@link CYCLE_ID_HEADER}.
 * Absence is fine — the client falls back to the legacy id header until the
 * backend endpoint ships.
 */
export const STAGING_CYCLE_TOKEN_STORAGE_KEY = "germanos.staging.cycleToken";

/** Header name — must match what grm-bff expects (set by Stage 1h). */
export const STAGE_ENV_HEADER = "X-Stage-Env";
export const STAGE_ENV_VALUE = "staging";
export const CYCLE_ID_HEADER = "X-Staging-Cycle-Id";
/**
 * HMAC token header — paired with the backend agent's signed-token endpoint.
 * When the session carries a token, this header replaces {@link CYCLE_ID_HEADER}
 * on every intercepted request.
 */
export const CYCLE_TOKEN_HEADER = "X-Staging-Cycle-Token";

/**
 * URL substrings that trigger the fetch interceptor to stamp staging headers.
 * Matches anything containing "/api/" (all BFF traffic) or the env BFF base URL.
 * Third-party calls (analytics, fonts, etc.) stay untouched.
 *
 * <p>`/touch/getMarketingText` is the legacy ATG servlet path grm-bff still
 * exposes for imported SiteBuilder promo scripts. It carries no `/api/`
 * segment, so without listing it explicitly those calls fell outside the
 * interception scope and answered from the LIVE marketing-text SKU lists even
 * mid-preview — which hid promo blocks whose visibility depends on those lists.
 */
export const INTERCEPTED_URL_MARKERS = ["/api/", "/touch/getMarketingText"] as const;

/** HTTP status returned by grm-bff when the cycle id is invalid / expired. */
export const CYCLE_EXPIRED_STATUS = 410;

/**
 * sessionStorage key holding the active pre-staging project slot (1..{@link MAX_PRESTAGING_SLOT}).
 * Set when the admin opens {@code /staging?slot=N} from the Projects Dashboard.
 * When present, the fetch + XHR interceptors stamp {@link SLOT_HEADER} on every
 * BFF call so grm-bff and downstream services route reads to
 * {@code germanos_pre_staging_NN}.
 */
export const STAGING_SLOT_STORAGE_KEY = "germanos.staging.slot";

/**
 * SessionStorage key that holds the human-readable project name for the
 * active pre-staging slot — read by {@code StagingPreviewBanner} so the
 * preview banner can render 'Pre-Staging — &lt;ProjectName&gt;' instead
 * of the bare 'Project N' it used to show with just the slot number.
 *
 * <p>Written by {@code installSlotBootstrap} when the storefront enters
 * via {@code /staging?slot=N&project=&lt;encoded-name&gt;}. Cleared
 * alongside the slot whenever the slot itself is cleared, so the two
 * stay in lockstep.
 */
export const STAGING_PROJECT_NAME_STORAGE_KEY = "germanos.staging.projectName";

/** Header name — must match {@code MongoRoutingInterceptor.SLOT_HEADER} in grm-common-library. */
export const SLOT_HEADER = "X-Project-Slot";

/**
 * Deployable ceiling for the pre-staging slot number — the single source of
 * truth for every slot upper-bound check in this feature ({@code readSlot},
 * {@code writeSlot}, {@code installSlotBootstrap}). Mirrors
 * {@code RoutingMongoDatabaseFactory.MAX_SLOT_NUMBER} (10) in grm-common-library
 * and the admin UI's {@code MAX_SLOT}.
 *
 * <p>This is the CEILING (max registered slot DBs), NOT the per-env active
 * count. Prod runs 10 slots; local/UAT run 5, but they simply never emit a
 * slot number above 5, so clamping to 10 everywhere is safe — the backend's
 * env-driven {@code getSlotCount()} is the authority that rejects out-of-range
 * slots. Historically some readers capped at a stale 5, silently downgrading
 * slot-6..10 previews/writes to shared staging.
 */
export const MAX_PRESTAGING_SLOT = 10;

/** Where the user is bounced to after exit (or on 410). */
export const LIVE_HOME_PATH = "/";

/**
 * Path of the HMAC token validation endpoint. Today the only deployment is on
 * admin-bff ({@code /api/admin/staging/cycle/validate-token}). The storefront
 * calls it directly; grm-bff doesn't expose a storefront-facing passthrough yet.
 * Kept as a constant so the literal {@code validate-token} appears in a single
 * place for grep audits, and so a future grm-bff proxy can be swapped in by
 * changing one line.
 */
export const STAGING_VALIDATE_TOKEN_PATH = "/api/admin/staging/cycle/validate-token";

/**
 * Default storefront BFF (grm-bff) base URL. The validate-token endpoint is
 * exposed on grm-bff (which proxies to admin-bff via {@code StagingCycleValidator})
 * so the storefront UI follows the same single-origin convention as every
 * other backend call: build with {@code localhost:4002} fallback, then
 * {@code serve.js} substitutes {@code GATSBY_CLIENT_TARGET} at request time.
 *
 * <p>Going direct to admin-bff (port 4007) was a bug — the storefront UI's
 * {@code serve.js} only rewrites {@code localhost:4002}, so a {@code 4007}
 * literal escaped into the deployed bundle and the browser tried to call
 * the user's own laptop.
 */
export const DEFAULT_ADMIN_BFF_BASE_URL = "http://localhost:4002";
